import React from 'react';
import styled from 'styled-components/macro';
import Colors from '../../../internal-components/Colors';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import AttachmentIcon from '@material-ui/icons/Attachment';
import CloseIcon from '@material-ui/icons/Close';
import useTranslations from '../../../hooks/useTranslations';
import Animation from '../../Animation';
import { EndpointTypes, getEndpointType } from '../FormEndpoints';
import { getEncodedToken } from '../../../utils/getJWT';
import { FormContext } from '../FormContext';
import { formatFileSize } from '../engine/FileUploadFieldEngine';

const DropTarget = styled.div`
  min-height: 120px;
  background: ${Colors.BACKGROUND_GREY};
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  padding: 12px;
  ${({ isDragActive }) =>
    isDragActive &&
    'animation: pulse 2s ease-in-out infinite alternate; border: 2px dashed ' +
      Colors.PRIMARY +
      ';'}

  transition: border 0.2s;

  @keyframes pulse {
    0% {
      background: ${Colors.BACKGROUND_GREY};
    }
    50% {
      background-color: #eaeeff;
    }
    100% {
      background-color: ${Colors.BACKGROUND_GREY};
    }
  }

  &:focus {
    outline: none;
  }

  a {
    cursor: pointer;
  }

  .dropzone-text {
    font-size: 14px;
  }

  [class*='MuiCollapse'] {
    width: 100%;
  }
`;

const CloudUpload = styled(CloudUploadIcon)`
  && {
    font-size: 48px;
  }
`;

const FileUploadFieldView = props => {
  const { getRootProps, getInputProps, isDragActive } = props.dropzone;
  const t = useTranslations();
  return (
    <DropTarget
      {...getRootProps({ onClick: props.onFieldClick })}
      isDragActive={isDragActive}
      onClick={props.onFieldClick}
    >
      <input
        {...getInputProps()}
        data-testid="dropzone-input"
        disabled={false}
      />
      <CloudUpload />
      <p className="dropzone-text">
        <Link
          className="dropzone-button"
          onClick={() => {
            !props.readOnly && props.dropzone.open();
          }}
        >
          {props.attachmentCount
            ? t('forms.field.fileupload.upload.more')
            : t('forms.field.fileupload.upload.first')}
        </Link>
        {' ' + t('forms.field.fileupload.upload.draghere')}
      </p>
      {props.attachments.map(file => (
        <FileCard
          key={file.id}
          file={file}
          deleteAPI={() => {
            if (props.readOnly) {
              return;
            }
            props.deleteFile(file.id);
          }}
          submissionId={props.submissionId}
          readOnly={props.readOnly}
        />
      ))}
    </DropTarget>
  );
};

const FileCardPaper = styled(Paper)`
  && {
    width: 100%;
    margin: 12px 0 0;
    padding: 8px 12px;
    display: flex;
    align-items: center;

    .FCP__svg {
      opacity: 0.3;
      margin-right: 1ch;
    }

    .FCP__filename {
      font-weight: bold;
      flex: 1;
      text-align: left;

      /* https://css-tricks.com/snippets/css/prevent-long-urls-from-breaking-out-of-container/ */
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-all;
      word-break: break-word;

      a {
        font-weight: bold;
      }
    }

    .FCP__size {
      opacity: 0.5;
      font-weight: 400;
      padding: 0 0.5ch;
      word-break: keep-all;
      overflow-wrap: normal;
      word-wrap: normal;
    }

    .FCP__progress-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: none;
      width: 200px;
      max-width: 50vw;
      padding: 1ch;
    }
  }
`;
const SmallIcon = styled(IconButton)`
  && {
    font-size: 12px;
    padding: 7px;
    margin: -6px;

    svg {
      font-size: 16px;
    }
  }
`;

const FileCard = ({ file, deleteAPI, submissionId, readOnly }) => {
  let { name, size, id, progress, complete, fieldId } = file;
  const { formId, endpointType } = React.useContext(FormContext);
  const [isInDeleteSequence, setIsInDeleteSequence] = React.useState(false);

  const url =
    process.env.REACT_APP_API_BASE +
    '/forms/v1/files/forms/' +
    formId +
    '/submissions/' +
    submissionId +
    '/' +
    fieldId +
    '_' +
    id;

  const filename =
    complete &&
    (endpointType || getEndpointType()) !== EndpointTypes.ANONYMOUS ? (
      <Link
        href={url + `?jwt=${getEncodedToken()}`}
        title="Download"
        target="_blank"
        rel="noopener noreferrer"
        download
        data-testid="file-download-link"
      >
        {name}
      </Link>
    ) : (
      name
    );

  return (
    <Animation type="collapse" in={!isInDeleteSequence} onExited={deleteAPI}>
      <Animation type="zoom" in={!isInDeleteSequence} appear>
        <FileCardPaper>
          <AttachmentIcon className="FCP__svg" />
          <span className="FCP__filename">
            {filename}
            <span className="FCP__size">&nbsp;({formatFileSize(size)})</span>
          </span>

          <Animation type="zoom" in={!complete}>
            <span
              className="FCP__progress-wrapper"
              data-testid={'file-progress'}
            >
              {progress < 99 ? (
                <LinearProgress
                  style={{ width: '100%' }}
                  variant="determinate"
                  value={progress}
                  data-testid={'file-progress-uploading'}
                />
              ) : (
                <LinearProgress
                  style={{ width: '100%' }}
                  data-testid={'file-progress-waiting'}
                />
              )}
            </span>
          </Animation>
          {!readOnly && (
            <SmallIcon
              onClick={() => {
                setIsInDeleteSequence(true);
              }}
              data-testid={'delete-file'}
            >
              <CloseIcon />
            </SmallIcon>
          )}
        </FileCardPaper>
      </Animation>
    </Animation>
  );
};

export default FileUploadFieldView;
